import { logError } from "@/logging/logging";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { useCallback, useEffect, useState } from "react";
import type { FeatureTourViewerTopic } from "./feature-tour-data";
import {
  flattenFueFlags,
  splitFueFlags,
  type FeatureTourStatuses,
  type FeatureTourViewStatus,
  type FueFlags,
} from "./fue-flags";

/** The status of loading the feature tour flags of the current user. */
type FetchStatus = "uninitialized" | "loading" | "loaded" | "failed";

type UseFeatureTours = {
  /** Whether the statuses, which feature tours the user has seen, have been loaded. */
  areVisibilityStatusesLoaded: boolean;

  /** Which feature tours the user has already skipped or completed. */
  visibilityStatuses: FeatureTourStatuses;

  /** Persistently remembers that the current user has skipped or completed a feature tour. */
  markStatus(
    topicId: FeatureTourViewerTopic,
    status: FeatureTourViewStatus,
  ): void;
};

/**
 * Loads and persists the status of the feature tour topics for the current user.
 *
 * The statuses are stored in the FUE flags of the user, so that they are shared across devices.
 *
 * @returns The statuses of all topics and a callback to remember that a tour was seen
 */
export function useFeatureTours(): UseFeatureTours {
  const { coreApiClient } = useApiClientContext();
  const currentUser = useAppSelector(selectCurrentUser);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>("uninitialized");
  const [flags, setFlags] = useState<FueFlags>({ known: {}, other: {} });

  useEffect(() => {
    if (!currentUser || fetchStatus !== "uninitialized") return;

    setFetchStatus("loading");
    coreApiClient
      .getUserFueFlags()
      .then((fueFlags) => {
        setFlags(splitFueFlags(fueFlags));
        setFetchStatus("loaded");
      })
      .catch((error) => {
        logError(error);
        setFetchStatus("failed");
      });
  }, [coreApiClient, currentUser, fetchStatus]);

  const markStatus = useCallback(
    (topicId: FeatureTourViewerTopic, status: FeatureTourViewStatus) => {
      setFlags((current) => {
        const updated: FueFlags = {
          known: { ...current.known, [topicId]: status },
          other: current.other,
        };

        // The backend replaces all flags of the user, so the flags of other apps have to be sent too
        coreApiClient.setUserFueFlags(flattenFueFlags(updated)).catch(logError);

        return updated;
      });
    },
    [coreApiClient],
  );

  return {
    areVisibilityStatusesLoaded: fetchStatus === "loaded",
    visibilityStatuses: flags.known,
    markStatus,
  };
}
